import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Card,
    CardContent,
    CircularProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import MarketingService, { type Partner, type PartnerStatement } from '../../services/marketing/marketingService.ts';
import { errorMessage, euro, fmtDateTime, thisMonth } from './marketingShared.ts';

const service = new MarketingService();
const num = { fontVariantNumeric: 'tabular-nums' } as const;

/**
 * A partner's statement for one month: the delivered orders of the customers
 * the partner brought, inside the attribution window, with the commission on
 * each. The partner and the month sit in the address, so a statement can be
 * linked to from the partner's page.
 */
const PartnerStatementPage = () => {
    const { t } = useTranslation();
    const [params, setParams] = useSearchParams();
    const partnerId = Number(params.get('partnerId')) || null;
    const month = params.get('month') || thisMonth();

    const [partners, setPartners] = useState<Partner[] | null>(null);
    const [partnersFailed, setPartnersFailed] = useState(false);
    const [statement, setStatement] = useState<PartnerStatement | null>(null);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState<string | null>(null);

    useEffect(() => {
        service.partners().then(setPartners).catch(() => setPartnersFailed(true));
    }, []);

    useEffect(() => {
        if (!partnerId || !/^\d{4}-\d{2}$/.test(month)) {
            setStatement(null);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setFailed(null);
        service.statement(partnerId, month)
            .then((s) => { if (!cancelled) setStatement(s); })
            .catch((e) => {
                if (cancelled) return;
                setStatement(null);
                setFailed(errorMessage(e, t('marketing.statement.loadFailed')));
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [partnerId, month, t]);

    const chosen = useMemo(
        () => partners?.find((p) => p.id === partnerId) ?? null,
        [partners, partnerId]
    );

    const update = (changes: Record<string, string | null>) => {
        const next = new URLSearchParams(params);
        Object.entries(changes).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
        setParams(next, { replace: true });
    };

    const partner = statement?.partner ?? chosen;

    return (
        <Box sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <ReceiptLongIcon />
                <Typography variant="h5">{t('resources.partnerStatement')}</Typography>
            </Stack>

            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <Autocomplete
                            sx={{ minWidth: 320, flex: 1 }}
                            size="small"
                            options={partners ?? []}
                            loading={!partners && !partnersFailed}
                            value={chosen}
                            getOptionLabel={(p) => `${p.name} (${p.code})${p.isActive ? '' : ` · ${t('marketing.partners.inactive')}`}`}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            onChange={(_, p) => update({ partnerId: p ? String(p.id) : null })}
                            renderInput={(p) => <TextField {...p} label={t('marketing.statement.partner')} />}
                        />
                        <TextField
                            label={t('marketing.statement.month')}
                            type="month"
                            size="small"
                            value={month}
                            onChange={(e) => update({ month: e.target.value || null })}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Stack>
                    {partnersFailed && (
                        <Alert severity="warning" sx={{ mt: 2 }}>{t('marketing.statement.partnersFailed')}</Alert>
                    )}
                </CardContent>
            </Card>

            {failed && <Alert severity="error" sx={{ mb: 2 }}>{failed}</Alert>}

            {!partnerId && <Alert severity="info">{t('marketing.statement.pickPartner')}</Alert>}

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            )}

            {!loading && statement && (
                <Card>
                    <CardContent>
                        <Typography variant="h6">{partner?.name} ({partner?.code})</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {t('marketing.statement.terms', {
                                rate: Number(partner?.ratePercent ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 2 }),
                                days: partner?.attributionDays ?? 0,
                            })}
                        </Typography>
                        <Box sx={{ overflowX: 'auto' }}>
                            <Table size="small" sx={{ minWidth: 720 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('marketing.statement.deliveredAt')}</TableCell>
                                        <TableCell>{t('marketing.statement.order')}</TableCell>
                                        <TableCell>{t('marketing.statement.customer')}</TableCell>
                                        <TableCell>{t('marketing.statement.venue')}</TableCell>
                                        <TableCell align="right">{t('marketing.statement.foodSubtotal')}</TableCell>
                                        <TableCell align="right">{t('marketing.statement.commission')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {statement.orders.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {t('marketing.statement.empty')}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {statement.orders.map((o) => (
                                        <TableRow key={o.orderId} hover>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDateTime(o.deliveredAt)}</TableCell>
                                            <TableCell>{o.orderNumber}</TableCell>
                                            <TableCell>{o.customerName ?? `#${o.customerId}`}</TableCell>
                                            <TableCell>{o.locationName ?? '—'}</TableCell>
                                            <TableCell align="right" sx={num}>{euro(o.foodSubtotal)}</TableCell>
                                            <TableCell align="right" sx={num}>{euro(o.commission)}</TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid var(--color-secondary)' } }}>
                                        <TableCell colSpan={4}>
                                            {t('marketing.statement.totals', { count: statement.totals.orders })}
                                        </TableCell>
                                        <TableCell align="right" sx={num}>{euro(statement.totals.foodSales)}</TableCell>
                                        <TableCell align="right" sx={num}>{euro(statement.totals.commission)}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {t('marketing.statement.note')}
                        </Typography>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default PartnerStatementPage;
